// Sisa uang kembali pemasok — K214 / BUSINESS-87 (keputusan Owner 13 Okt 2026): uang kembali boleh dicatat beberapa
// kali per retur/uang muka sampai jumlahnya terpenuhi; sisa yang belum diterima DITAGIH ADMIN PURCHASING.
//
// Panel ini daftar tagihnya: sumber yang belum terpenuhi (belum ada uang masuk sama sekali, atau baru sebagian),
// dengan sisa per sumber. Finance tetap pencatat uangnya (layar Uang Masuk); panel ini baca saja. Wewenang baca
// dijaga backend (`GET /uang-masuk/uang-kembali-vendor/sisa`). MODE KLIEN (pola VendorTelatPanel): himpunannya
// kecil dan dihitung utuh di server.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HandCoins } from "lucide-react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { formatIDR, formatDate } from "../../lib/format";
import { apiErrorMessage } from "../../lib/purchasingApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const KOLOM = [
  { kunci: "nomor", judul: "Nomor" },
  { kunci: "vendor_nama", judul: "Pemasok" },
  { kunci: "nilai_diharapkan_idr", judul: "Disepakati" },
  { kunci: "total_diterima_idr", judul: "Sudah diterima" },
  { kunci: "sisa_idr", judul: "Sisa" },
  { kunci: "sejak", judul: "Sejak", ambil: (s) => formatDate(s.sejak), nilai: (s) => s.sejak },
];

/** `sumber` = "RETUR_VENDOR" atau "UANG_MUKA_VENDOR" — hanya sumber itu yang ditampilkan di layar pemanggilnya. */
export default function SisaUangKembaliPanel({ sumber, testId = "sisa-uang-kembali" }) {
  const [items, setItems] = useState(null);
  const daftar = useDaftar(items || [], {
    awalan: "su_", ukuran: 10, kolom: KOLOM, kunciCari: ["nomor", "vendor_nama"],
    urutAwal: { kunci: "sejak", arah: "asc" },
  });

  const muat = useCallback(async () => {
    try {
      const r = await api.get("/uang-masuk/uang-kembali-vendor/sisa");
      setItems((r.data.items || []).filter((s) => !sumber || s.sumber === sumber));
    } catch (e) {
      setItems([]);
      toast.error(apiErrorMessage(e, "Daftar sisa uang kembali belum bisa dimuat."));
    }
  }, [sumber]);
  useEffect(() => { muat(); }, [muat]);

  if (items === null || items.length === 0) return null;
  const totalSisa = items.reduce((a, s) => a + Number(s.sisa_idr || 0), 0);
  const tautan = (s) => (s.sumber === "RETUR_VENDOR" ? "/purchasing/returns" : `/purchasing/dp/${s.sumber_id}`);

  return (
    <Card className="p-4 space-y-3" data-testid={testId}>
      <div className="font-medium flex items-center gap-2">
        <HandCoins className="h-4 w-4" /> Uang kembali yang belum diterima ({items.length}) · sisa {formatIDR(totalSisa)}
      </div>
      <p className="text-xs text-muted-foreground">
        Pemasok belum mengembalikan seluruh uang yang disepakati. Tagih sisanya ke pemasok; Finance mencatat tiap
        uang yang masuk di layar Uang Masuk sampai jumlahnya terpenuhi.
      </p>
      <div className="overflow-x-auto">
        <PalangDaftar daftar={daftar} placeholder="Cari nomor atau pemasok…" testId={`${testId}-palang`}
                      ekspor={{ nama: "sisa_uang_kembali", kolom: KOLOM }} />
        <table className="w-full text-sm" data-testid={`${testId}-tabel`}>
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <ThUrut daftar={daftar} kunci="nomor" className="py-1 pr-2">Nomor</ThUrut>
              <ThUrut daftar={daftar} kunci="vendor_nama" className="py-1 pr-2">Pemasok</ThUrut>
              <ThUrut daftar={daftar} kunci="nilai_diharapkan_idr" align="right" className="py-1 pr-2 text-right">Disepakati</ThUrut>
              <ThUrut daftar={daftar} kunci="total_diterima_idr" align="right" className="py-1 pr-2 text-right">Sudah diterima</ThUrut>
              <ThUrut daftar={daftar} kunci="sisa_idr" align="right" className="py-1 pr-2 text-right">Sisa</ThUrut>
              <ThUrut daftar={daftar} kunci="sejak" className="py-1">Sejak</ThUrut>
            </tr>
          </thead>
          <tbody>
            {daftar.tampil.length === 0 && (
              <tr><td colSpan={6} className="py-2 text-muted-foreground">Tidak ada yang cocok.</td></tr>
            )}
            {daftar.tampil.map((s) => (
              <tr key={`${s.sumber}-${s.sumber_id}`} className="border-b last:border-0" data-testid={`${testId}-${s.sumber_id}`}>
                <td className="py-1 pr-2">
                  <Link to={tautan(s)} className="font-mono underline">{s.nomor || "—"}</Link>
                  {s.sebagian && <StatusBadge tone="warning" className="ml-1">Baru sebagian</StatusBadge>}
                </td>
                <td className="py-1 pr-2">{s.vendor_nama || "—"}</td>
                <td className="py-1 pr-2 text-right font-mono">{formatIDR(s.nilai_diharapkan_idr)}</td>
                <td className="py-1 pr-2 text-right font-mono">{formatIDR(s.total_diterima_idr)}</td>
                <td className="py-1 pr-2 text-right font-mono font-medium">{formatIDR(s.sisa_idr)}</td>
                <td className="py-1">{formatDate(s.sejak)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId={`${testId}-paginasi`} />
      </div>
    </Card>
  );
}
